<template>
  <div class="main-box" v-if="!form">
    <b-alert variant="warning" show class="my-0" >
      Sie haben keinen Zugriff
    </b-alert>
  </div>
  <div class="main-box" v-else>
    <h1>Person bearbeiten</h1>
    <div class="my-2">
      <b-button to="/person">Zurück</b-button>
    </div>
    <b-alert v-model="hasUnknownError" variant="danger">
      {{ unknownErrorMessage }}
    </b-alert>

    <validation-observer ref="observer" v-slot="{ passes }">
      <b-form @submit.prevent="passes(onSubmit)">
        <validation-provider vid="anrede">
          <b-form-group id="input-group-anrede" label="Anrede" label-for="input-anrede">
            <b-form-select v-model="form.anrede" :options="titles" id="input-anrede"></b-form-select>
          </b-form-group>
        </validation-provider>

        <validation-provider vid="vorname" rules="required" v-slot="{ valid, errors, changed }">
          <b-form-group id="input-group-vorname" label="Vorname *" label-for="input-vorname">
            <b-form-input
              id="input-vorname"
              v-model="form.vorname"
              :state="onValidateField(valid, errors, changed)"
              type="text"
              placeholder="Vorname angeben"
            ></b-form-input>
            <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <validation-provider vid="name" rules="required" v-slot="{ valid, errors, changed }">
          <b-form-group id="input-group-name" label="Nachname *" label-for="input-name">
            <b-form-input
              id="input-name"
              v-model="form.name"
              :state="onValidateField(valid, errors, changed)"
              type="text"
              placeholder="Nachname angeben"
            ></b-form-input>
            <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <b-form-group id="input-group-firma" label="Firma" label-for="input-firma">
          <b-form-input
            id="input-firma"
            v-model="form.firma"
            type="text"
            placeholder="Firma angeben"
          ></b-form-input>
        </b-form-group>

        <b-form-group id="input-group-zusatz" label="Zusatz" label-for="input-zusatz">
          <b-form-input
            id="input-zusatz"
            v-model="form.zusatz"
            type="text"
            placeholder="Zusatz angeben"
          ></b-form-input>
        </b-form-group>

        <b-form-group id="input-group-uid" label="UID" label-for="input-uid">
          <b-form-input
            id="input-uid"
            v-model="form.uid"
            type="text"
            placeholder="UID angeben"
          ></b-form-input>
        </b-form-group>

        <b-form-group id="input-group-oeffentliche-hand" label="öffentliche Hand" label-for="input-oeffentliche-hand">
          <b-form-checkbox
            id="input-oeffentliche-hand"
            v-model="form.oeffentlicheHand"
            :value="true"
            :unchecked-value="false"
          />
        </b-form-group>

        <b-row>
          <b-col cols="9">
            <validation-provider vid="strasse" rules="required" v-slot="{ valid, errors, changed }">
              <b-form-group id="input-group-strasse" label="Strasse *" label-for="input-strasse">
                <b-form-input
                  id="input-strasse"
                  v-model="form.strasse"
                  :state="onValidateField(valid, errors, changed)"
                  type="text"
                  placeholder="Strasse angeben"
                ></b-form-input>
                <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col class="col">
            <b-form-group id="input-group-haus-nr" label="Nr." label-for="input-haus-nr">
              <b-form-input
                id="input-haus-nr"
                v-model="form.hausNr"
                type="text"
                placeholder="Nr. angeben"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="4">
            <validation-provider vid="plz" rules="required|numeric" v-slot="{ valid, errors, changed }">
              <b-form-group id="input-group-plz" label="PLZ *" label-for="input-plz">
                <b-form-input
                  id="input-plz"
                  v-model="form.plz"
                  :state="onValidateField(valid, errors, changed)"
                  type="text"
                  placeholder="PLZ angeben"
                ></b-form-input>
                <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
          <b-col class="col">
            <validation-provider vid="ort" rules="required" v-slot="{ valid, errors, changed }">
              <b-form-group id="input-group-ort" label="Ort *" label-for="input-ort">
                <b-form-input
                  id="input-ort"
                  v-model="form.ort"
                  :state="onValidateField(valid, errors, changed)"
                  type="text"
                  placeholder="Ort angeben"
                ></b-form-input>
                <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <validation-provider vid="telefon" rules="phoneNumber" v-slot="{ valid, errors, changed }">
          <b-form-group id="input-group-telefon" label="Telefon *" label-for="input-telefon">
            <b-form-input
              id="input-telefon"
              v-model="form.telefon"
              :state="onValidateField(valid, errors, changed)"
              type="text"
              placeholder="Telefonnr. angeben"
            ></b-form-input>
            <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <validation-provider vid="email" rules="email" v-slot="{ valid, errors, changed }">
          <b-form-group id="input-group-email" label="E-Mail (Empfohlen, wenn Sie Gesuche zum Signieren an diese Person weiterleiten möchten)" label-for="input-email">
            <b-form-input
              v-model="form.email"
              :state="onValidateField(valid, errors, changed)"
              type="email"
              placeholder="E-Mail angeben"
            ></b-form-input>
            <b-form-invalid-feedback class="input-live-feedback">{{ errors[0] }}</b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <validation-provider vid="land">
          <b-form-group id="input-group-land" label="Land" label-for="input-land">
            <b-form-select v-model="form.land" :options="countries"></b-form-select>
          </b-form-group>
        </validation-provider>

        <b-button type="submit" variant="primary" disabled v-if="isSubmitting">
          <b-spinner small></b-spinner>
          Person speichern
        </b-button>
        <b-button type="submit" variant="primary" v-else>Person speichern</b-button>

      </b-form>
    </validation-observer>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { errorHandler, validateField } from '@/helpers/formHelper.js'

  export default {
    components: {
      ValidationObserver,
      ValidationProvider
    },
    data: () => ({
      isSubmitting: false,
      hasUnknownError: false,
      unknownErrorMessage: '',
      hasError: false,
      errorMessage: '',
      countries: [
        { value: 16, text: 'Schweiz' },
        { value: 17, text: 'Deutschland' },
        { value: 18, text: 'Österreich' },
        { value: 19, text: 'Lichtenstein' },
        { value: 21, text: 'Frankreich' },
        { value: 20, text: 'Italien' },
        { value: 92, text: 'Israel' },
        { value: 91, text: 'USA' },
        { value: 241, text: 'Grossbritanien' }
      ],
      titles: [
        { value: 'Herr', text: 'Herr' },
        { value: 'Frau', text: 'Frau' },
        { value: 'Herr & Frau', text: 'Herr & Frau' }
      ]
    }),
    created () {
      const { personId } = this.$route.params
      this.getPerson(personId)
    },
    methods: {
      getPerson (personId) {
        this.hasUnknownError = false
        this.hasError = false

        this.$store.dispatch('person/GET_PERSON_BY_ID', { personId, includeOrganisation: false }).then(res => {
          const { errors } = res

          if (errors) {
            return errorHandler(this, errors)
          }
        }).catch(err => {
          this.hasUnknownError = true
          this.unknownErrorMessage = this.$t('formError.unknownwError')
          console.error(err)
        })
      },
      scrollTo () {
        this.$scrollTo('.logo')
      },
      onSubmit () {
        this.isSubmitting = true
        this.hasUnknownError = false
        this.hasError = false

        this.$store.dispatch('person/UPDATE_PERSON', this.form).then(res => {
          const { errors } = res

          if (errors) {
            this.isSubmitting = false
            this.scrollTo()
            return errorHandler(this, errors)
          }

          this.isSubmitting = false
          this.$router.push({ name: 'personen' }).catch((e) => {})
        }).catch(err => {
          this.hasUnknownError = true
          this.unknownErrorMessage = this.$t('formError.unknownwError')
          this.isSubmitting = false
          this.scrollTo()
          console.error(err)
        })
      },
      onValidateField (valid, errors, changed) {
        return validateField(valid, errors, changed)
      },
      reset () {
        requestAnimationFrame(() => {
          this.$refs.observer.reset()
        })
      }
    },
    computed: {
      ...mapGetters({ form: 'person/PERSON' })
    }
  }
</script>

<style lang="scss" scoped>
  .optional{
    text-decoration: underline;
  }
</style>
